/**
 * BrightPath - Expenses API Client
 * BP-04-04: Expenses Frontend
 */

import apiClient from '@/core/lib/api-client';
import type {
  Expense,
  ExpenseCreate,
  ExpenseUpdate,
  ExpenseListParams,
  ExpenseListResponse,
  ExpenseSummary,
  ExpenseTrend,
  CategorySummary,
  ChildSummary,
} from '../types';

// Note: apiClient already has /api/v1 as base URL
const BASE_URL = '';

/**
 * Get family expenses with filtering and pagination
 */
export async function getExpenses(
  familyId: string,
  params?: ExpenseListParams
): Promise<ExpenseListResponse> {
  const searchParams = new URLSearchParams();
  
  if (params) {
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.pageSize) searchParams.set('page_size', params.pageSize.toString());
    if (params.sortBy) searchParams.set('sort_by', params.sortBy);
    if (params.sortOrder) searchParams.set('sort_order', params.sortOrder);
    if (params.category) searchParams.set('category', params.category);
    if (params.childId) searchParams.set('child_id', params.childId);
    if (params.startDate) searchParams.set('start_date', params.startDate);
    if (params.endDate) searchParams.set('end_date', params.endDate);
    if (params.minAmount) searchParams.set('min_amount', params.minAmount.toString());
    if (params.maxAmount) searchParams.set('max_amount', params.maxAmount.toString());
    if (params.isTaxDeductible !== null && params.isTaxDeductible !== undefined) {
      searchParams.set('is_tax_deductible', params.isTaxDeductible.toString());
    }
    if (params.isInsuranceCovered !== null && params.isInsuranceCovered !== undefined) {
      searchParams.set('is_insurance_covered', params.isInsuranceCovered.toString());
    }
    if (params.vendorName) searchParams.set('vendor_name', params.vendorName);
    if (params.tags?.length) searchParams.set('tags', params.tags.join(','));
    if (params.search) searchParams.set('search', params.search);
  }

  const query = searchParams.toString();
  const url = `${BASE_URL}/families/${familyId}/expenses${query ? `?${query}` : ''}`;
  
  return apiClient.get<ExpenseListResponse>(url);
}

/**
 * Get a single expense by ID
 */
export async function getExpense(expenseId: string): Promise<Expense> {
  return apiClient.get<Expense>(`${BASE_URL}/expenses/${expenseId}`);
}

/**
 * Create a new expense
 */
export async function createExpense(
  familyId: string,
  data: ExpenseCreate
): Promise<Expense> {
  return apiClient.post<Expense>(`${BASE_URL}/families/${familyId}/expenses`, data);
}

/**
 * Update an existing expense
 */
export async function updateExpense(
  expenseId: string,
  data: ExpenseUpdate
): Promise<Expense> {
  return apiClient.patch<Expense>(`${BASE_URL}/expenses/${expenseId}`, data);
}

/**
 * Delete an expense
 */
export async function deleteExpense(expenseId: string): Promise<void> {
  return apiClient.delete(`${BASE_URL}/expenses/${expenseId}`);
}

/**
 * Bulk create expenses
 */
export async function bulkCreateExpenses(
  familyId: string,
  expenses: ExpenseCreate[]
): Promise<{ created: number; errors: { index: number; message: string }[] }> {
  return apiClient.post(`${BASE_URL}/families/${familyId}/expenses/bulk`, { expenses });
}

/**
 * Bulk delete expenses
 */
export async function bulkDeleteExpenses(
  familyId: string,
  expenseIds: string[]
): Promise<{ deleted: number }> {
  return apiClient.delete(`${BASE_URL}/families/${familyId}/expenses/bulk`, {
    data: { expense_ids: expenseIds },
  });
}

/**
 * Get expense summary statistics
 */
export async function getExpenseSummary(
  familyId: string,
  params?: {
    startDate?: string;
    endDate?: string;
    year?: number;
    month?: number;
  }
): Promise<ExpenseSummary> {
  const searchParams = new URLSearchParams();
  
  if (params) {
    if (params.startDate) searchParams.set('start_date', params.startDate);
    if (params.endDate) searchParams.set('end_date', params.endDate);
    if (params.year) searchParams.set('year', params.year.toString());
    if (params.month) searchParams.set('month', params.month.toString());
  }

  const query = searchParams.toString();
  return apiClient.get<ExpenseSummary>(
    `${BASE_URL}/families/${familyId}/expenses/summary${query ? `?${query}` : ''}`
  );
}

/**
 * Get expenses by category breakdown
 */
export async function getExpensesByCategory(
  familyId: string,
  params?: {
    startDate?: string;
    endDate?: string;
    year?: number;
  }
): Promise<CategorySummary[]> {
  const searchParams = new URLSearchParams();
  
  if (params) {
    if (params.startDate) searchParams.set('start_date', params.startDate);
    if (params.endDate) searchParams.set('end_date', params.endDate);
    if (params.year) searchParams.set('year', params.year.toString());
  }

  const query = searchParams.toString();
  return apiClient.get<CategorySummary[]>(
    `${BASE_URL}/families/${familyId}/expenses/by-category${query ? `?${query}` : ''}`
  );
}

/**
 * Get expenses by child breakdown
 */
export async function getExpensesByChild(
  familyId: string,
  params?: {
    startDate?: string;
    endDate?: string;
    year?: number;
  }
): Promise<ChildSummary[]> {
  const searchParams = new URLSearchParams();
  
  if (params) {
    if (params.startDate) searchParams.set('start_date', params.startDate);
    if (params.endDate) searchParams.set('end_date', params.endDate);
    if (params.year) searchParams.set('year', params.year.toString());
  }

  const query = searchParams.toString();
  return apiClient.get<ChildSummary[]>(
    `${BASE_URL}/families/${familyId}/expenses/by-child${query ? `?${query}` : ''}`
  );
}

/**
 * Get expense trends over time
 */
export async function getExpenseTrends(
  familyId: string,
  params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  }
): Promise<ExpenseTrend[]> {
  const searchParams = new URLSearchParams();
  
  if (params) {
    if (params.startDate) searchParams.set('start_date', params.startDate);
    if (params.endDate) searchParams.set('end_date', params.endDate);
    if (params.groupBy) searchParams.set('group_by', params.groupBy);
  }

  const query = searchParams.toString();
  return apiClient.get<ExpenseTrend[]>(
    `${BASE_URL}/families/${familyId}/expenses/trends${query ? `?${query}` : ''}`
  );
}

/**
 * Import expenses from CSV
 */
export async function importExpensesPreview(
  familyId: string,
  file: File
): Promise<import('../types').ImportPreview> {
  const formData = new FormData();
  formData.append('file', file);

  return apiClient.post(`${BASE_URL}/families/${familyId}/expenses/import/preview`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function importExpensesConfirm(
  familyId: string,
  file: File,
  options?: {
    skipDuplicates?: boolean;
    defaultCategory?: string;
  }
): Promise<import('../types').ImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.skipDuplicates) formData.append('skip_duplicates', 'true');
  if (options?.defaultCategory) formData.append('default_category', options.defaultCategory);

  return apiClient.post(`${BASE_URL}/families/${familyId}/expenses/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export const expensesApi = {
  getExpenses,
  getExpense,
  createExpense,
  updateExpense,
  deleteExpense,
  bulkCreateExpenses,
  bulkDeleteExpenses,
  getExpenseSummary,
  getExpensesByCategory,
  getExpensesByChild,
  getExpenseTrends,
  importExpensesPreview,
  importExpensesConfirm,
};
